import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';

export default class IntegrationAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  declare model: string;

  /**
   * Almost all of our existing integrations are company wide, which only global admins should be able to install.
   * Only webhooks are location-specific, which both global admins and location admins should be able to install.
   */
  get canInstall(): boolean {
    const { roleNames: roles } = this.currentAdmin;
    return roles.includes(GLOBAL_ADMIN);
  }

  /**
   * Directory integrations (Google Workspace, OneLogin, Okta, Microsoft Entra ID, Delinea)
   * should only be visible to global admins, not location admins.
   */
  get canViewDirectoryIntegrations(): boolean {
    const { roleNames: roles } = this.currentAdmin;
    return roles.includes(GLOBAL_ADMIN);
  }

  /**
   * Billing entitlement for the HRIS integration install. Billed under
   * hr-recruiting-integrations (Visitors Standard-1+ / Platform Enterprise),
   * not the directory-sync codes the other provider tiles use. Same flag-gated
   * rollout shape as the directory/SAML gates: flag off preserves the legacy
   * directory-sync gate.
   */
  get canAccessHris(): boolean {
    if (this.featureFlags.isEnabled('app-store-integrations-feature-gating')) {
      return Boolean(this.state.features?.canAccessHrRecruitingIntegrations);
    }
    return Boolean(this.state.features?.canAccessDirectorySync);
  }
}
